import { castDraft, castImmutable, current, Draft, immerable, isDraft, WritableDraft } from 'immer'
import { createContext, Dispatch, useEffect } from 'react'
import { useImmerReducer } from 'use-immer'
import { Camera } from './Camera.ts'
import { Decoder } from './Coding.ts'
import { Entity, Hit, noHit, nullEntity } from "./Entity.ts"
import { Floor } from './Floor.tsx'
import { Mannequin, nullMannequin, nullSkeleton } from './Mannequin.tsx'
import { Pose } from './Pose.ts'
import { PosedMannequin, PosedSkeleton } from './PosedSkeleton.ts'
import { Timeline } from './Timeline/Timeline.ts'
import { Updater } from './Updater.ts'
import { Quat, Ray, vec2, vec3, vec4 } from './VectorMath.ts'
import { decodeViewportSettingsFrom, ViewportSettings, ViewportSettings_fallback } from './Viewport.tsx'
import { WorldAxes } from './WorldAxes.tsx'

export const AppActionContext = createContext<Dispatch<AppAction>>((_action) => {
  throw new Error(`AppActionContext not provided`)
})

export function useAppStateReducer(): [AppState, Dispatch<AppAction>] {
  const answer = useImmerReducer((draft: Draft<AppState>, action: AppAction): void => {
    appStateUpdater.update(draft, action)
  }, null, () => new AppState({
    settings: Decoder.decodeInstanceFromLocalStorage(
      'appSettings',
      decodeAppSettingsFrom,
      AppSettings_fallback
    ),
  }))

  const [state, dispatch] = answer
  useSaveSettingsEffect(state)
  useLoadMannequinEffect(state, dispatch)

  return answer
}

export class AppState {
  readonly [immerable] = true

  readonly floor = new Floor()
  readonly worldAxes = new WorldAxes()

  readonly loadingError: Error | null = null
  readonly mannequin: Mannequin = nullMannequin
  readonly mouse: MouseState = defaultMouseState
  readonly settings: AppSettings

  readonly selectedBone: number | null = null

  readonly pose: Pose
  readonly posedSkeleton: PosedSkeleton
  readonly posedMannequin: PosedMannequin

  // While playing, this is the timestamp corresponding to time 0 of the timeline. While paused, this is null.
  readonly playTimeBase: DOMHighResTimeStamp | null = null

  constructor(props: {
    settings: AppSettings
  }) {
    this.settings = props.settings
    this.pose = this.settings.timeline.poseAtTime(this.settings.playheadTime)
    this.posedSkeleton = new PosedSkeleton(this.mannequin.skeleton, this.pose)
    this.posedMannequin = new PosedMannequin(this.mannequin, this.pose)
  }
}

export const models: string[] = [
  'head',
  'long_cubes',
  'mapped_cube',
  'robot',
  'simple_art',
  'split_cube',
  'wolf',
]

export interface AppSettings {
  readonly viewport: ViewportSettings
  readonly selectedModel: string
  readonly timeline: Timeline
  readonly playheadTime: number
}

function decodeAppSettingsFrom(decoder: Decoder): AppSettings {
  const container = decoder.keyedDecoder()
  const timeline = container.decodeInstance(
    'timeline',
    Timeline.decodeFrom,
    () => AppSettings_fallback.timeline
  )
  const playheadTime = container.decodeNumber('playheadTime', 0)
  return {
    viewport: container.decodeInstance('viewport', decodeViewportSettingsFrom),
    selectedModel: container.decodeString('selectedModel', models[0]),
    timeline: timeline,
    playheadTime: Math.max(0, Math.min(playheadTime, timeline.duration)),
  }
}

const AppSettings_fallback: AppSettings = {
  viewport: ViewportSettings_fallback,
  selectedModel: models[2],
  timeline: Timeline.makeInitialTimeline(nullSkeleton, 'null'),
  playheadTime: 0,
}

export interface MouseState {
  // The ray that passes through all points under the mouse pointer.
  readonly worldRay: Ray | null

  readonly hit: Hit
  readonly mode: MouseMode
}

type MouseMode =
  | {
    // Mouse button is not down.
    type: 'idle'
  } | {
    // Mouse button is down but no movement has been reported since it went down.
    type: 'pendingClick'
  }
  | MouseModeNavigate
  | MouseModeDragBone

type MouseModeNavigate = {
  // Mouse button is down and movement will adjust the camera.
  type: 'navigate'

  // Center of rotation for moving the camera. If infinite, the camera should rotate around its own position.
  orbitCenter: vec3
}

type MouseModeDragBone = {
  // Mouse button is down and movement will move a bone.
  type: 'dragBone'

  // The index of the bone being posed.
  bone: number
}

export type KeyboardModifiers = {
  shift: boolean
}

const defaultMouseState: MouseState = {
  worldRay: null,
  hit: noHit,
  mode: { type: 'idle' }
}

export type AppAction =
  | { type: 'animatePlayheadTime' }
  | { type: 'deleteCurrentKeyframe' }
  | {
    type: 'extendTimeline'
    seconds: number
  }
  | { type: 'goToNextKeyframe' }
  | { type: 'goToPriorKeyframe' }
  | { type: 'lookAtOrigin' }
  | {
    type: 'pickModel'
    value: string
  }
  | { type: 'reportMouseDown' }
  | MouseMoveAction
  | { type: 'reportMouseUp' }
  | {
    type: 'reportScrollGesture'
    deltaY: number
  }
  | { type: 'resetCamera' }
  | { type: 'resetPose' }
  | { type: 'resetSelectedBone' }
  | {
    type: 'rollSelectedBone'

    // Time elapsed since last roll for current keypress. Negative for opposite roll direction.
    dt: number
  }
  | {
    type: 'setAspectRatio',
    ratio: number
  }
  | {
    type: 'setBoolSetting'
    keyPath: string[]
    value: boolean
  }
  | {
    type: 'setPlayheadTime'
    time: number
  }
  | {
    type: 'setMannequin'
    model: string
    value: Mannequin | Error | null
  }
  | {
    type: 'setOpacity'
    opacity: number
  }
  | { type: 'togglePlayPause' }
  | {
    type: 'translateCamera'
    offset: vec3
  }

export type MouseMoveAction = {
  type: 'reportMouseMove'
  ndc: vec2
  keyboardModifiers: KeyboardModifiers
  movement: vec2
}

const appStateUpdater = new Updater<AppState, AppAction>((draft, action) => {
  switch (action.type) {
  case 'animatePlayheadTime':
    if (draft.playTimeBase !== null) {
      const maxTime = draft.settings.timeline.duration
      const newTime = Math.max(0, Math.min((performance.now() - draft.playTimeBase) / 1000, maxTime))
      draft.settings.playheadTime = newTime
      if (newTime === maxTime) {
        draft.playTimeBase = null
      }
    }
    break
  case 'deleteCurrentKeyframe':
    if (draft.settings.timeline.canDeleteKeyframeAtTime(draft.settings.playheadTime)) {
      draft.settings.timeline = draft.settings.timeline.withoutKeyframeAtTime(draft.settings.playheadTime)
    }
    break

  case 'extendTimeline':
    draft.settings.timeline = draft.settings.timeline.withTimelineExtendedBySeconds(action.seconds)
    break

  case 'goToNextKeyframe': {
    const i = draft.settings.timeline.keyframes.findIndex(kf => kf.time > draft.settings.playheadTime)
    if (i !== -1) {
      skipToPlayheadTime(draft, draft.settings.timeline.keyframes[i].time)
    }
    break
  }

  case 'goToPriorKeyframe': {
    const i = draft.settings.timeline.keyframes.findLastIndex(kf => kf.time < draft.settings.playheadTime)
    if (i >= 0) {
      skipToPlayheadTime(draft, draft.settings.timeline.keyframes[i].time)
    }
    break
  }

  case 'lookAtOrigin':
    draft.settings.viewport.camera = draft.settings.viewport.camera.lookingAt(vec3.zero)
    break

  case 'pickModel':
    draft.settings.selectedModel = action.value
    draft.mannequin = castDraft(nullMannequin)
    draft.loadingError = null
    draft.selectedBone = null
    break

  case 'reportMouseDown':
    draft.mouse.mode = { type: 'pendingClick' }
    break

  case 'reportMouseMove':
    handleMouseMove(draft, action)
    break

  case 'reportMouseUp':
    if (draft.mouse.mode.type === 'pendingClick') {
      const hit = draft.mouse.hit
      draft.selectedBone = hit.who instanceof PosedSkeleton ? hit.who.pickBone(hit.extra) : null
    }

    draft.mouse.mode = { type: 'idle' }
    if (draft.mouse.worldRay !== null) {
      draft.mouse.hit = firstHit(draft.mouse.worldRay, draft)
    }
    break

  case 'reportScrollGesture':
    handleScrollGesture(draft, action.deltaY)
    break

  case 'resetCamera':
    draft.settings.viewport.camera = new Camera({
      ...Camera.fallback,
      aspectRatio: draft.settings.viewport.camera.aspectRatio
    })
    break

  case 'resetPose':
    setCurrentPose(draft, Pose.forSkeleton(draft.mannequin.skeleton))
    break

  case 'resetSelectedBone': {
    const b = draft.selectedBone
    if (b === null || b < 0 || b >= draft.pose.bonePoses.length) {
      break
    }
    mutateCurrentPose(draft, pose => {
      pose.bonePoses[b] = Quat.identity
    })
    break
  }

  case 'rollSelectedBone': {
    const b = draft.selectedBone
    if (b === null || b < 0 || b >= draft.pose.bonePoses.length) {
      break
    }
    const radiansPerSecond = Math.PI * 0.5
    const rotation = Quat.rotation(vec3.yPlus, action.dt * radiansPerSecond)
    mutateCurrentPose(draft, pose => {
      pose.bonePoses[b] = pose.bonePoses[b].times(rotation)
    })
    break
  }

  case 'setAspectRatio':
    draft.settings.viewport.camera = new Camera({
      ...draft.settings.viewport.camera,
      aspectRatio: action.ratio
    })
    break

  case 'setBoolSetting': {
    let child = draft.settings as any
    for (const key of action.keyPath.slice(0, -1)) {
      child = child[key]
    }
    child[action.keyPath[action.keyPath.length - 1]] = action.value
    break
  }

  case 'setPlayheadTime':
    draft.settings.playheadTime = Math.max(0, Math.min(action.time, draft.settings.timeline.duration))
    break

  case 'setMannequin': {
    if (draft.settings.selectedModel !== action.model) {
      break
    }

    draft.selectedBone = null

    const result = action.value
    if (!result) {
      draft.loadingError = null
      draft.mannequin = castDraft(nullMannequin)
    } else if (result instanceof Error) {
      draft.loadingError = result
      draft.mannequin = castDraft(nullMannequin)
    } else {
      draft.loadingError = null
      draft.mannequin = castDraft(result)
    }
    break
  }

  case 'setOpacity':
    draft.settings.viewport.meshView.opacity = action.opacity
    break

  case 'togglePlayPause':
    if (draft.playTimeBase === null) {
      if (draft.settings.playheadTime >= draft.settings.timeline.duration) {
        draft.settings.playheadTime = 0
      }
      draft.playTimeBase = performance.now() - draft.settings.playheadTime * 1000
    } else {
      draft.playTimeBase = null
    }
    break

  case 'translateCamera':
    draft.settings.viewport.camera = draft.settings.viewport.camera.translatedInViewSpace(action.offset)
    break

  default:
    return action satisfies never // compile-time exhaustiveness check
  }
}).then(draft => {
  if (!draft.settings.timeline.isCompatibleWith(draft.mannequin.skeleton, draft.settings.selectedModel)) {
    draft.settings.timeline = Timeline.makeInitialTimeline(draft.mannequin.skeleton, draft.settings.selectedModel)
  }
}).onChangeOf(
  draft => draft.settings.timeline,
  (_oldTimeline, draft) => {
    draft.playTimeBase = null
    draft.pose = draft.settings.timeline.poseAtTime(draft.settings.playheadTime)
  }
).onChangeOf(
  draft => draft.mannequin,
  (_oldMannequin, draft) => {
    draft.playTimeBase = null
  }
).onChangeOf(
  draft => draft.settings.playheadTime,
  (_oldPlayheadTime, draft) => {
    draft.pose = draft.settings.timeline.poseAtTime(draft.settings.playheadTime)
  }
).then(draft => {
  const newPose = isDraft(draft.pose) ? current(draft.pose) : draft.pose
  if (draft.posedMannequin.mannequin !== draft.mannequin || draft.posedMannequin.pose !== newPose) {
    draft.posedMannequin = castDraft(new PosedMannequin(castImmutable(draft.mannequin) as Mannequin, newPose))
    draft.posedSkeleton = new PosedSkeleton(draft.mannequin.skeleton, newPose)
  }
}).then(draft => {
  if (draft.settings.playheadTime < 0 || draft.settings.playheadTime > draft.settings.timeline.duration) {
    draft.settings.playheadTime = Math.max(0, Math.min(draft.settings.playheadTime, draft.settings.timeline.duration))
  }
})

function skipToPlayheadTime(draft: WritableDraft<AppState>, time: number): void {
  time = Math.max(0, Math.min(time, draft.settings.timeline.duration))
  draft.settings.playheadTime = time
  if (draft.playTimeBase !== null) {
    draft.playTimeBase = performance.now() - time * 1000
  }
}

function setCurrentPose(draft: WritableDraft<AppState>, pose: Pose): void {
  const [timeline, i] = draft.settings.timeline.withKeyframeAtTime(draft.settings.playheadTime)
  draft.settings.timeline = timeline
  draft.settings.timeline.keyframes[i].pose = pose
}

function mutateCurrentPose(draft: WritableDraft<AppState>, mutate: (pose: WritableDraft<Pose>) => void): void {
  const [timeline, i] = draft.settings.timeline.withKeyframeAtTime(draft.settings.playheadTime)
  draft.settings.timeline = timeline
  const mutablePose = draft.settings.timeline.keyframes[i].pose
  mutate(mutablePose)
}

function handleMouseMove(draft: WritableDraft<AppState>, action: MouseMoveAction): void {
  const ray = draft.settings.viewport.camera.worldRayForNdc(action.ndc)
  draft.mouse.worldRay = ray

  switch (draft.mouse.mode.type) {

  case 'dragBone':
    return handleDragBone(draft, draft.mouse.mode, action)

  case 'idle':
    // No mouse button down. Just track what the mouse is over.
    draft.mouse.hit = firstHit(ray, draft)
    return

  case 'navigate':
    return handleDragNavigate(draft, draft.mouse.mode, action)

  case 'pendingClick':
    if (
      draft.mouse.hit.who === draft.posedSkeleton
      && draft.selectedBone !== null
      && draft.mouse.hit.extra === draft.selectedBone
    ) {
      draft.mouse.mode = {
        type: 'dragBone',
        bone: draft.selectedBone,
      }
      return handleDragBone(draft, draft.mouse.mode, action)
    }

    draft.mouse.mode = {
      type: 'navigate',
      orbitCenter: draft.mouse.hit.where
    }
    return handleDragNavigate(draft, draft.mouse.mode, action)
  }
}

function handleDragNavigate(draft: WritableDraft<AppState>, mode: MouseModeNavigate, action: MouseMoveAction): void {
  const { x, y } = action.movement
  if (action.keyboardModifiers.shift) {
    const panRate = 0.002 // view space units per mouse point
    draft.settings.viewport.camera = draft.settings.viewport.camera.panned(x * -panRate, y * panRate, 0)
  } else {
    const orbitRate = 0.0005 // radians per mouse point
    draft.settings.viewport.camera = draft.settings.viewport.camera.orbited({
      dAzimuth: x * -orbitRate,
      dAltitude: y * -orbitRate,
      around: mode.orbitCenter
    })
  }
}

function handleDragBone(draft: WritableDraft<AppState>, mode: MouseModeDragBone, action: MouseMoveAction): void {
  const movementLength = action.movement.length
  if (movementLength === 0) {
    return
  }
  // Note that y and x are swapped here to produce a rotation axis perpendicular to the mouse motion.
  const vAxis = new vec4(action.movement.y, action.movement.x, 0, 0).normalized
  const radiansPerPoint = 0.001
  const radians = movementLength * radiansPerPoint
  const wAxis = draft.settings.viewport.camera.worldFromView.times(vAxis).normalized.xyz
  const boneFromWorld = draft.posedSkeleton.worldFromBone_posedTransforms()[mode.bone].basis.inverse
  const bAxis = boneFromWorld.times(wAxis)
  const rotation = Quat.rotation(bAxis, radians)
  mutateCurrentPose(draft, pose => {
    pose.bonePoses[mode.bone] = pose.bonePoses[mode.bone].times(rotation)
  })
}

function handleScrollGesture(draft: WritableDraft<AppState>, deltaY: number): void {
  const ray = draft.mouse.worldRay
  if (!ray) {
    return
  }

  const { where } = draft.mouse.hit
  if (draft.mouse.hit.when === Infinity) {
    const zoomRate = 0.02 // view space units per wheel unit
    const offset = ray.direction.times(deltaY * -zoomRate)
    draft.settings.viewport.camera = draft.settings.viewport.camera.translatedInViewSpace(offset)
    return
  }

  // Zoom asymptotically toward or away from the world-space hit position.

  const camera = draft.settings.viewport.camera
  const t = Math.exp(0.0005 * deltaY)
  const newPosition = camera.position.times(t).plus(where.times(1 - t))
  const newCamera = new Camera({ ...camera, position: newPosition })

  // Try to prevent zooming in far enough to clip the point under the mouse.
  const where4 = where.asPoint
  const mouseInView = camera.viewFromWorld.times(where4)
  const mouseInNewView = newCamera.viewFromWorld.times(where4)
  if (mouseInView.z >= 1.05 * camera.near && mouseInNewView.z > mouseInView.z) {
    // Already clipping or close to clipping so don't zoom in even more.
    return
  }

  draft.settings.viewport.camera = newCamera
}

function firstHit(ray: Ray, draft: WritableDraft<AppState>): Hit {
  function firstHit(ray: Ray, entities: Entity[]): Hit {
    return entities.reduce((bestHit, entity) => {
      const hit = entity.hitTest(ray)
      return hit.when >= 0 && hit.when < bestHit.when ? hit : bestHit
    }, noHit)
  }

  // Prefer hitting a bone if the mesh is transparent.
  const meshIsVisible = draft.settings.viewport.meshView.opacity > 0.0
  const meshIsTransparent = draft.settings.viewport.meshView.opacity < 1.0
  const prioritySkeleton = draft.settings.viewport.bonesEnabled && meshIsTransparent ? draft.posedSkeleton : nullEntity
  const skeleton = prioritySkeleton === nullEntity && draft.settings.viewport.bonesEnabled ? draft.posedSkeleton : nullEntity
  const mannequin = meshIsVisible ? draft.posedMannequin : nullEntity
  const floor = draft.settings.viewport.floorEnabled ? draft.floor : nullEntity

  const priorityHit = firstHit(ray, [prioritySkeleton])
  if (priorityHit !== noHit) {
    return priorityHit
  }

  return firstHit(ray, [skeleton, mannequin, floor])
}

function useSaveSettingsEffect(state: AppState): void {
  useEffect(() => {
    const json = JSON.stringify(state.settings)
    localStorage.setItem('appSettings', json)
  }, [state.settings])
}

function useLoadMannequinEffect(state: AppState, sendAppAction: Dispatch<AppAction>): void {
  const selectedModel = state.settings.selectedModel
  useEffect(() => {
    // React async pattern from https://stackoverflow.com/a/66071205/77567

    let active = true
    sendAppAction({ type: 'setMannequin', model: selectedModel, value: null })

    async function load() {
      const url = `./models/${state.settings.selectedModel}.dae`
      try {
        const mannequin = await Mannequin.load(url)
        if (active) {
          sendAppAction({ type: 'setMannequin', model: selectedModel, value: mannequin })
        }
      } catch (error) {
        if (!active) {
          return
        }
        if (!(error instanceof Error)) {
          error = new Error(`unknown error while loading ${url}: ${error}`)
        }
        sendAppAction({ type: 'setMannequin', model: selectedModel, value: error as Error })
      }
    }

    load()
    return () => { active = false }
  }, [
    selectedModel,
    // Mannequin is here so hot reloads that change the Mannequin class trigger a reload.
    Mannequin
  ])
}

// Find the ray under the mouse traveling perpendicular to the screen in NDC and transform it back to world space.
export function worldRayForNdc(props: {
  ndc: vec2
  camera: Camera
}): Ray {
  // This calculation is based on the use of reverse Z and infinite far plane.

  const { camera, ndc } = props

  const negativeNear = -camera.near

  // Perspective un-divide, assuming we want to get a point that is "on the screen" where the view volume extends back from the screen surface.
  const xc = negativeNear * ndc.x
  const yc = negativeNear * ndc.y

  // The ray under the mouse, directly down the view volume, starts at the near plane.
  const r0 = camera.worldFromView
    .times(camera.viewFromClip)
    .times(new vec4(xc, yc, negativeNear, negativeNear))
    .xyz

  const rd = camera.worldFromView
    .times(camera.viewFromClip)
    .times(new vec4(xc, yc, 0, negativeNear))
    .xyz

  return new Ray(r0, rd)
}
